import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Select, SelectOption, Slider, Switcher } from "@/shared/ui";
import SettingsControlLabel from "@/domains/chat/components/settings/SettingsLabel";
import SettingsTooltip from "@/domains/chat/components/settings/SettingsTooltip";
import useChatValues from "@/domains/settings/chat/hooks/useChatValues";
import { appliedChatSettingsSelector, setAppliedSettings } from "@/domains/settings/chat/slice";

export default function SettingsBodyCommonContainer() {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const {
    fontSizeValues,
    fontWeightValues,
    opacityTextValues,
    chatHeightSizeValues,
    chatWidthSizeValues,
    opacityBackgroundValues,
  } = useChatValues();
  const {
    fontSize,
    fontWeight,
    opacityText,
    chatHeightSize,
    chatWidthSize,
    opacityBackground,
    opacityChatModeWithoutAction,
    showCommandTooltip,
    notifyInRolledUpMode,
  } = useAppSelector(appliedChatSettingsSelector);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <SettingsControlLabel>{t("chat.settings.fontSize")}</SettingsControlLabel>
        <Slider
          value={fontSize}
          values={fontSizeValues}
          min={12}
          max={24}
          step={2}
          onChange={(value) => dispatch(setAppliedSettings({ fontSize: value }))}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-12 font-bold uppercase"
        />
      </div>
      <div>
        <SettingsControlLabel>{t("chat.settings.fontWeight")}</SettingsControlLabel>
        <Select value={fontWeight} className="mt-3 z-3" bg="bg-dark-800">
          <div className="flex flex-col">
            {fontWeightValues.map((item) => (
              <SelectOption
                key={item.value}
                onClick={() => dispatch(setAppliedSettings({ fontWeight: item.value }))}
                isSelected={item.value === fontWeight}
                className="hover:text-accent-solid transition-all"
              >
                {t(item.label)}
              </SelectOption>
            ))}
          </div>
        </Select>
      </div>
      <div>
        <SettingsControlLabel>{t("chat.settings.opacityText")}</SettingsControlLabel>
        <Slider
          value={opacityText}
          values={opacityTextValues}
          min={0}
          max={100}
          step={5}
          onChange={(value) => dispatch(setAppliedSettings({ opacityText: value }))}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-12 font-bold uppercase"
        />
      </div>
      <div>
        <SettingsControlLabel>{t("chat.settings.chatHeight")} </SettingsControlLabel>
        <Slider
          value={chatHeightSize}
          values={chatHeightSizeValues}
          min={0}
          max={100}
          step={1}
          onChange={(value) => dispatch(setAppliedSettings({ chatHeightSize: value }))}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-12 font-bold uppercase"
        />
      </div>
      <div>
        <SettingsControlLabel>{t("chat.settings.chatWidth")}</SettingsControlLabel>
        <Slider
          value={chatWidthSize}
          values={chatWidthSizeValues}
          min={0}
          max={100}
          step={1}
          onChange={(value) => dispatch(setAppliedSettings({ chatWidthSize: value }))}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-12 font-bold uppercase"
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <SettingsControlLabel>{t("chat.settings.opacityBackground")}</SettingsControlLabel>
          <SettingsTooltip text={t("chat.settings.opacityBackgroundTooltip")} />
        </div>
        <Slider
          value={opacityBackground}
          values={opacityBackgroundValues}
          min={0}
          max={100}
          step={5}
          onChange={(value) => dispatch(setAppliedSettings({ opacityBackground: value }))}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-12 font-bold uppercase"
        />
      </div>
      <div className="w-full h-fit flex flex-col rounded-[0.625rem] border border-dark-800">
        <div className="h-[3.125rem] px-3.5 flex items-center">
          <div className="text-14 font-bold">{t("chat.settings.opacityChatModeWithoutAction")}</div>
          <SettingsTooltip
            text={t("chat.settings.opacityChatModeWithoutActionTooltip")}
            className="ml-auto"
          />
          <Switcher
            on={opacityChatModeWithoutAction}
            onChange={() =>
              dispatch(
                setAppliedSettings({ opacityChatModeWithoutAction: !opacityChatModeWithoutAction }),
              )
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center  border-t border-dark-800">
          <div className="text-14 font-bold">{t("chat.settings.showCommand")}</div>
          <SettingsTooltip text={t("chat.settings.showCommandTooltip")} className="ml-auto" />
          <Switcher
            on={showCommandTooltip}
            onChange={() =>
              dispatch(setAppliedSettings({ showCommandTooltip: !showCommandTooltip }))
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center  border-t border-dark-800">
          <div className="text-14 font-bold">{t("chat.settings.notifyInRolledUpMode")}</div>
          <SettingsTooltip
            text={t("chat.settings.notifyInRolledUpModeTooltip")}
            className="ml-auto"
          />
          <Switcher
            on={notifyInRolledUpMode}
            onChange={() =>
              dispatch(setAppliedSettings({ notifyInRolledUpMode: !notifyInRolledUpMode }))
            }
            withBorder
          />
        </div>
      </div>
    </div>
  );
}
